import {
  type AccountInfo,
  type EventMessage,
  EventType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

/**
 * Entra sign-in (spec §5.6): auth-code + PKCE against the single tenant. Authorization comes
 * from app roles in the token (DecoStop.*) — Global Admins are auto-granted server-side; the
 * portal only decorates UI, every check is enforced by the API.
 */
export const devBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === '1';

export const apiScopes = [`api://${import.meta.env.VITE_ENTRA_API_CLIENT_ID}/.default`];

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: import.meta.env.VITE_ENTRA_PORTAL_CLIENT_ID ?? '00000000-0000-0000-0000-000000000000',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID ?? 'common'}`,
    redirectUri: window.location.origin,
    // Land back on the portal after sign-out (which renders the sign-in page) rather than on
    // Microsoft's generic "you signed out" screen.
    postLogoutRedirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'sessionStorage' },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (_level, message, containsPii) => {
        if (!containsPii) console.warn('[msal]', message);
      },
    },
  },
});

/**
 * Keep exactly one account "active". Without this, token acquisition falls back to
 * getAllAccounts()[0], which silently picks an arbitrary account when someone has signed in
 * with more than one — and leaves `getActiveAccount()` null for the UI.
 */
export function bindActiveAccount(): void {
  const existing = msalInstance.getActiveAccount();
  if (!existing) {
    const [first] = msalInstance.getAllAccounts();
    if (first) msalInstance.setActiveAccount(first);
  }
  msalInstance.addEventCallback((event: EventMessage) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload &&
      'account' in event.payload &&
      event.payload.account
    ) {
      msalInstance.setActiveAccount(event.payload.account as AccountInfo);
    }
    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      msalInstance.setActiveAccount(null);
    }
  });
}

export function currentAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

export async function acquireApiToken(): Promise<string | null> {
  if (devBypass) return null;
  const account = currentAccount();
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch {
    await msalInstance.acquireTokenRedirect({ scopes: apiScopes });
    return null;
  }
}

/** Full sign-out: clears the MSAL cache and ends the Entra session. */
export async function signOut(): Promise<void> {
  const account = currentAccount();
  await msalInstance.logoutRedirect({
    account: account ?? undefined,
    postLogoutRedirectUri: window.location.origin,
  });
}
